import { useMemo, useState } from 'react';
import { useLanguage } from '../i18n';
import type { DocumentSection } from '../utils/markdownParser';
import { Search, ChevronRight, ChevronDown } from 'lucide-react';

interface DocumentOutlineProps {
  sections: DocumentSection[];
  activeId?: string;
  onNavigate: (id: string) => void;
}

function useSectionFilter(sections: DocumentSection[]) {
  const [query, setQuery] = useState('');

  const visibleIds = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    const ids = new Set<string>();
    function walk(list: DocumentSection[]): boolean {
      let any = false;
      for (const section of list) {
        const selfMatch = section.title.toLowerCase().includes(q);
        const subMatch = walk(section.subsections);
        if (selfMatch || subMatch) {
          ids.add(section.id);
          any = true;
        }
      }
      return any;
    }
    walk(sections);
    return ids;
  }, [query, sections]);

  return { query, setQuery, visibleIds };
}

function renderTree(
  sections: DocumentSection[],
  activeId: string | undefined,
  onNavigate: (id: string) => void,
  expanded: Set<string>,
  toggle: (id: string) => void,
  visibleIds: Set<string> | null,
  depth = 0
) {
  return sections.map((section) => {
    const isActive = activeId === section.id;
    const hasSubs = section.subsections.length > 0;
    const isExpanded = expanded.has(section.id) || visibleIds !== null;
    const isVisible = visibleIds === null || visibleIds.has(section.id);

    if (!isVisible) return null;

    return (
      <div key={section.id} className={depth > 0 ? 'ml-3 border-l border-cloud pl-2' : ''}>
        <div className="flex items-center">
          {hasSubs && (
            <button
              onClick={() => toggle(section.id)}
              className="p-1 text-ash hover:text-ink"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
          <button
            onClick={() => onNavigate(section.id)}
            className={`flex-1 text-left text-sm py-1.5 px-2 transition-colors ${
              isActive ? 'bg-red-50 text-red-900 font-medium' : 'text-slate hover:bg-mist hover:text-ink'
            }`}
          >
            <span className="line-clamp-2">{section.title}</span>
          </button>
        </div>
        {hasSubs && isExpanded &&
          renderTree(section.subsections, activeId, onNavigate, expanded, toggle, null, depth + 1)}
      </div>
    );
  });
}

export default function DocumentOutline({ sections, activeId, onNavigate }: DocumentOutlineProps) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(() => new Set<string>());
  const { query, setQuery, visibleIds } = useSectionFilter(sections);

  if (sections.length === 0) return null;

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <nav className="w-56 shrink-0 hidden xl:block">
      <div className="sticky top-6 bg-white border border-cloud p-4 shadow-sm">
        <h3 className="text-xs font-semibold text-slate uppercase tracking-wider mb-3">{t.document.outline}</h3>
        <div className="relative mb-3">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-ash" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.framework.searchPlaceholder}
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-cloud bg-paper focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
        </div>
        <div className="space-y-0.5 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
          {renderTree(sections, activeId, onNavigate, expanded, toggle, visibleIds)}
        </div>
      </div>
    </nav>
  );
}
